/** Logical world coordinates are independent of the 2x artwork/backing resolution. */
export const INTERNAL_W = 480;
export const INTERNAL_H = 270;
export const WORLD_SCALE = 2;
export const UI_SCALE = 2;
export const UI_W = INTERNAL_W * UI_SCALE;
export const UI_H = INTERNAL_H * UI_SCALE;

export interface Screen {
  canvas: HTMLCanvasElement; // world layer
  ctx: CanvasRenderingContext2D;
  ui: HTMLCanvasElement; // ui layer (2x)
  uictx: CanvasRenderingContext2D;
  display: HTMLCanvasElement;
  scale: number;
  toInternal(cx: number, cy: number): [number, number];
  present(): void;
}

const rasterScales = new WeakMap<HTMLCanvasElement, number>();
export const rasterScale = (c: HTMLCanvasElement): number => rasterScales.get(c) ?? 1;
export const rasterWidth = (c: HTMLCanvasElement): number => c.width / rasterScale(c);
export const rasterHeight = (c: HTMLCanvasElement): number => c.height / rasterScale(c);
export function tagRaster(c: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  rasterScales.set(c, scale);
  return c;
}
export const makeWorldCanvas = (w: number, h: number) => makeCanvas(w, h, WORLD_SCALE);

/** All source crop rectangles and destinations use logical coordinates. */
export function drawRaster(ctx: CanvasRenderingContext2D, src: HTMLCanvasElement, ...rect: number[]): void {
  const scale = rasterScale(src);
  if (rect.length === 2) ctx.drawImage(src, rect[0], rect[1], rasterWidth(src), rasterHeight(src));
  else if (rect.length === 4) ctx.drawImage(src, rect[0], rect[1], rect[2], rect[3]);
  else if (rect.length === 8) ctx.drawImage(src, rect[0] * scale, rect[1] * scale, rect[2] * scale, rect[3] * scale, rect[4], rect[5], rect[6], rect[7]);
  else throw new Error('drawRaster: expected a position, destination, or source/destination rectangles');
}
export function makeCanvas(w: number, h: number, scale = 1): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  return tagRaster(c, scale);
}
export function ctxOf(c: HTMLCanvasElement, opts?: CanvasRenderingContext2DSettings): CanvasRenderingContext2D {
  const ctx = c.getContext('2d', opts);
  if (!ctx) throw new Error('2d context unavailable');
  ctx.setTransform(rasterScale(c), 0, 0, rasterScale(c), 0, 0);
  ctx.imageSmoothingEnabled = false;
  return ctx;
}

export function createScreen(root: HTMLElement): Screen {
  const canvas = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  const ctx = ctxOf(canvas);
  const ui = makeCanvas(UI_W, UI_H);
  const uictx = ctxOf(ui);
  // Composite live layers 1:1. CSS handles the final pixel enlargement; fractional
  // drawImage scaling of nested canvas records can omit glyphs/sprite regions.
  const display = makeCanvas(UI_W, UI_H);
  display.id = 'display';
  root.appendChild(display);
  // Every frame paints the whole display, so an opaque backing store lets the browser
  // composite it without blending.
  const dctx = ctxOf(display, { alpha: false });

  const screen: Screen = {
    canvas, ctx, ui, uictx, display,
    scale: 1,
    toInternal(cx, cy) {
      const r = display.getBoundingClientRect();
      return [(cx - r.left) * INTERNAL_W / r.width, (cy - r.top) * INTERNAL_H / r.height];
    },
    present() {
      dctx.imageSmoothingEnabled = false;
      dctx.drawImage(canvas, 0, 0, display.width, display.height);
      dctx.drawImage(ui, 0, 0, display.width, display.height);
    },
  };

  function resize() {
    const ww = window.innerWidth, wh = window.innerHeight;
    // Integer scales keep pixels even on desktops; a phone would waste most of its
    // screen at 1x, so anything below 2x fills the window instead.
    let s = Math.floor(Math.min(ww / INTERNAL_W, wh / INTERNAL_H));
    if (s < 2) s = Math.min(ww / INTERNAL_W, wh / INTERNAL_H);
    screen.scale = s;
    const width = Math.round(INTERNAL_W * s), height = Math.round(INTERNAL_H * s);
    display.style.width = width + 'px';
    display.style.height = height + 'px';
    display.style.left = Math.floor((ww - width) / 2) + 'px';
    display.style.top = Math.floor((wh - height) / 2) + 'px';
    dctx.imageSmoothingEnabled = false;
  }
  window.addEventListener('resize', resize);
  resize();
  return screen;
}

/** Wait for the bitmap font to be usable before first render. */
export async function waitForFont(): Promise<void> {
  try {
    await document.fonts.load('16px NeoDGM');
    await document.fonts.ready;
  } catch {
    /* font missing: fall back to monospace */
  }
}
